import { useState, useEffect, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, Wrench } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ToolCategoryFilter, ToolGrid, ToolConfigModal } from '@/components/tools'
import { toolsService, type Tool, type ToolsByCategoryResponse } from '@/services/tools'
import { useOrganizationStore } from '@/stores/organization-store'
import { useDebounce } from '@/hooks/use-debounce'

// Sort options
type SortOption = 'name_asc' | 'name_desc' | 'created_desc' | 'created_asc'

// No organization selected state
function NoOrganizationState({ t }: { t: (key: string) => string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="w-20 h-20 rounded-2xl bg-amber-500/20 flex items-center justify-center mb-6">
        <Wrench className="w-10 h-10 text-amber-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-200 mb-2">
        {t('list.noProjectSelected')}
      </h3>
      <p className="text-gray-500 text-center max-w-md">
        {t('list.noProjectSelectedDesc')}
      </p>
    </div>
  )
}

export function ToolsPage() {
  const { t } = useTranslation('tools')
  const { currentOrganization } = useOrganizationStore()

  // Sort options
  const SORT_OPTIONS: { value: SortOption; label: string }[] = [
    { value: 'name_asc', label: t('sort.nameAsc') },
    { value: 'name_desc', label: t('sort.nameDesc') },
    { value: 'created_desc', label: t('sort.createdDesc') },
    { value: 'created_asc', label: t('sort.createdAsc') },
  ]

  // State
  const [tools, setTools] = useState<Tool[]>([])
  const [toolsByCategory, setToolsByCategory] = useState<ToolsByCategoryResponse[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [sortOption, setSortOption] = useState<SortOption>('name_asc')

  // Config modal state
  const [configModalOpen, setConfigModalOpen] = useState(false)
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null)

  // Debounced search
  const debouncedSearch = useDebounce(searchTerm, 300)

  // Determine if we should show grouped view
  const showGroupedView = !debouncedSearch && !selectedCategory

  // Fetch tools when organization or filters change
  useEffect(() => {
    const controller = new AbortController()

    async function fetchTools() {
      if (!currentOrganization?.id) {
        setTools([])
        setToolsByCategory([])
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        if (showGroupedView) {
          // Fetch grouped by category
          const response = await toolsService.listByCategory(
            currentOrganization.id,
            { signal: controller.signal }
          )
          setToolsByCategory(response)
          setTools([])
        } else {
          // Fetch flat list with filters
          const response = await toolsService.listTools(
            currentOrganization.id,
            {
              search: debouncedSearch || undefined,
              category: selectedCategory || undefined,
            },
            { signal: controller.signal }
          )
          setTools(response.tools)
          setToolsByCategory([])
        }
      } catch (error) {
        if ((error as Error).name !== 'CanceledError') {
          console.error('Failed to fetch tools:', error)
          setTools([])
          setToolsByCategory([])
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    fetchTools()

    return () => {
      controller.abort()
    }
  }, [currentOrganization?.id, debouncedSearch, selectedCategory, showGroupedView])

  // Sorted tools (for flat list view)
  const sortedTools = useMemo(() => {
    return [...tools].sort((a, b) => {
      switch (sortOption) {
        case 'name_asc':
          return a.name.localeCompare(b.name)
        case 'name_desc':
          return b.name.localeCompare(a.name)
        case 'created_desc':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        case 'created_asc':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        default:
          return 0
      }
    })
  }, [tools, sortOption])

  // Handle tool click
  const handleToolClick = (tool: Tool) => {
    // Open config modal if tool is configurable
    if (tool.tool_type === 'mcp' || tool.tool_type === 'api') {
      handleToolConfigure(tool)
    }
  }

  // Handle tool configure
  const handleToolConfigure = useCallback((tool: Tool) => {
    setSelectedTool(tool)
    setConfigModalOpen(true)
  }, [])

  // Handle config success - refresh tools list
  const handleConfigSuccess = useCallback(async () => {
    if (!currentOrganization?.id) return

    try {
      if (showGroupedView) {
        const response = await toolsService.listByCategory(currentOrganization.id)
        setToolsByCategory(response)
      } else {
        const response = await toolsService.listTools(currentOrganization.id, {
          search: debouncedSearch || undefined,
          category: selectedCategory || undefined,
        })
        setTools(response.tools)
      }
    } catch (error) {
      console.error('Failed to refresh tools:', error)
    }
  }, [currentOrganization, showGroupedView, debouncedSearch, selectedCategory])

  // No organization selected
  if (!currentOrganization) {
    return <NoOrganizationState t={t} />
  }

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex flex-col gap-4">
        {/* Search and Sort row */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input
              type="text"
              placeholder={t('list.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/5 border-gray-700/50 focus:border-cyan-500/50"
            />
          </div>

          {/* Sort (only visible when not grouped) */}
          {!showGroupedView && (
            <Select value={sortOption} onValueChange={(value) => setSortOption(value as SortOption)}>
              <SelectTrigger className="w-full sm:w-44 bg-white/5 border-gray-700/50">
                <SelectValue placeholder="Ordenar" />
              </SelectTrigger>
              <SelectContent className="glass border-gray-700/50">
                {SORT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="focus:bg-white/5">
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Category filter */}
        <ToolCategoryFilter
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />
      </div>

      {/* Tools Grid */}
      <ToolGrid
        tools={showGroupedView ? undefined : sortedTools}
        toolsByCategory={showGroupedView ? toolsByCategory : undefined}
        isLoading={isLoading}
        groupByCategory={showGroupedView}
        onToolClick={handleToolClick}
        onToolConfigure={handleToolConfigure}
      />

      {/* Config Modal */}
      {selectedTool && (
        <ToolConfigModal
          open={configModalOpen}
          onOpenChange={setConfigModalOpen}
          tool={selectedTool}
          organizationId={currentOrganization.id}
          onSuccess={handleConfigSuccess}
        />
      )}
    </div>
  )
}
